import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useStore } from 'react-redux';
import { getRelatedTo, skipFetchingParamData } from '#component/general/list-filter/helper';

const formatParams = filterOption => {
  const [key] = Object.keys(filterOption);
  const [params] = Object.values(filterOption);

  if (skipFetchingParamData(key)) {
    return {
      [key]: params
    };
  }

  const ids = params.map(param => param.data.id || param.data.uid).join(',');

  return {
    [key]: ids
  };
};

export const useUpdateURLParams = () => {
  const history = useHistory();

  const updateURL = useCallback(newParams => {
    const key = Object.keys(newParams);
    const searchParams = new URLSearchParams(window.location.search);
    const existingParams = Object.fromEntries(searchParams.entries());
    const updatedParams = { ...existingParams, ...newParams };
    const newSearchParams = new URLSearchParams(updatedParams);

    if (!newParams[key]) {
      newSearchParams.delete(key);
    }

    const newURL = `${window.location.pathname}?${newSearchParams.toString()}`;

    history.push(newURL);
  }, [history]);

  return useCallback(filterOption => {
    const formattedParams = formatParams(filterOption);
    updateURL(formattedParams);
  }, [updateURL]);
};

export const useUpdateURL = () => {
  const history = useHistory();

  return useCallback(async filter => {
    const searchParams = new URLSearchParams();
    const relatedTo = getRelatedTo();
    const pageFilter = filter?.[relatedTo];

    if (pageFilter) {
      return new Promise(resolve => {
        Object.keys(pageFilter).map(async filterKey => {
          const filterOptions = pageFilter[filterKey]?.value;

          if (skipFetchingParamData(filterKey) && filterOptions?.length) {
            searchParams.set(filterKey, filterOptions);
          } else if (filterOptions?.length) {
            const ids = filterOptions.map(param => param.data.id || param.data.uid).join(',');
            searchParams.set(filterKey, ids);
          }
        });

        const newURL = `${window.location.pathname}?${searchParams.toString()}`;
        history.push(newURL);
        resolve();
      });
    }

    return undefined;
  }, [history]);
};

export const useApplyFilterToUrl = () => {
  const store = useStore();
  const updateURL = useUpdateURL();

  return useCallback(() => {
    updateURL(store.getState().listFilter);
  }, [store, updateURL]);
};
